import React, { useState } from 'react';
import { X, Check, XCircle } from 'lucide-react';
import { useProgress } from '../context/ProgressContext';
import { CelebrationEffect } from './CelebrationEffect';
import { getQuizById } from '../data/quizzesData';
export function QuizPlayer({
  quizId,
  onExit
}: {
  quizId: string;
  onExit: () => void;
}) {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [score, setScore] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const {
    addPoints,
    completeQuiz,
    earnBadge
  } = useProgress();
  const quiz = getQuizById(quizId);
  if (!quiz) {
    return <div className="min-h-screen w-full bg-gradient-to-br from-red-900 to-pink-900 flex items-center justify-center p-4" data-id="element-213">
        <div className="bg-white dark:bg-slate-800 rounded-3xl p-8 text-center" data-id="element-214">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4" data-id="element-215">
            Quiz Not Found
          </h2>
          <button onClick={onExit} className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700" data-id="element-216">
            Go Back
          </button>
        </div>
      </div>;
  }
  const questions = quiz.questions;
  const question = questions[currentQuestion];
  const handleAnswer = (index: number) => {
    if (showExplanation) return;
    setSelectedAnswer(index);
    setShowExplanation(true);
    if (index === question.correct) {
      setScore(score + 1);
      addPoints(10);
    }
  };
  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer(null);
      setShowExplanation(false);
    } else {
      setIsComplete(true);
      completeQuiz(quizId);
      const bonusPoints = score === questions.length ? 50 : 20;
      addPoints(bonusPoints);
      if (score === questions.length) {
        earnBadge('perfect-score');
      }
      setShowCelebration(true);
    }
  };
  if (isComplete) {
    const percentage = Math.round(score / questions.length * 100);
    return <>
        <CelebrationEffect trigger={showCelebration} message={percentage === 100 ? 'Perfect Score!' : 'Quiz Complete!'} points={percentage === 100 ? 50 : 20} data-id="element-217" />
        <div className="min-h-screen w-full bg-gradient-to-br from-purple-900 via-blue-900 to-pink-900 flex items-center justify-center p-4" data-id="element-218">
          <div className="max-w-2xl w-full bg-white dark:bg-slate-800 rounded-3xl shadow-2xl p-8 text-center" data-id="element-219">
            <div className="text-6xl mb-4 animate-bounce" data-id="element-220">
              {percentage >= 80 ? '🎉' : percentage >= 60 ? '👏' : '💪'}
            </div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-221">
              Quiz Complete!
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 mb-6" data-id="element-222">
              You scored {score} out of {questions.length} ({percentage}%)
            </p>
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl p-6 mb-6" data-id="element-223">
              <p className="text-gray-700 dark:text-gray-300 mb-4" data-id="element-224">
                {percentage >= 80 ? "Amazing work! You're a true expert! 🌟" : percentage >= 60 ? 'Great job! Keep learning and growing! 📚' : 'Good effort! Review the topics and try again! 💪'}
              </p>
              {percentage === 100 && <p className="text-lg font-bold text-green-600 dark:text-green-400 mb-2" data-id="element-225">
                  Perfect Score! +50 bonus points! ⭐
                </p>}
              <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-226">
                💡 Continue your learning journey by exploring more quizzes and
                stories!
              </p>
            </div>
            <button onClick={onExit} className="px-8 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all" data-id="element-227">
              Back to Quizzes
            </button>
          </div>
        </div>
      </>;
  }
  return <div className="min-h-screen w-full bg-gradient-to-br from-green-900 via-blue-900 to-purple-900 flex items-center justify-center p-4" data-id="element-228">
      <div className="max-w-3xl w-full bg-white dark:bg-slate-800 rounded-3xl shadow-2xl overflow-hidden" data-id="element-229">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-blue-600 p-4 flex items-center justify-between" data-id="element-230">
          <div className="text-white" data-id="element-231">
            <span className="text-sm font-medium" data-id="element-232">
              Question {currentQuestion + 1} of {questions.length}
            </span>
            <div className="w-48 h-2 bg-white/20 rounded-full mt-1" data-id="element-233">
              <div className="h-full bg-white rounded-full transition-all" style={{
              width: `${(currentQuestion + 1) / questions.length * 100}%`
            }} data-id="element-234" />
            </div>
          </div>
          <button onClick={onExit} className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors" data-id="element-235">
            <X className="w-5 h-5 text-white" data-id="element-236" />
          </button>
        </div>
        {/* Question */}
        <div className="p-8" data-id="element-237">
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6" data-id="element-238">
            {question.question}
          </h3>
          {/* Options */}
          <div className="space-y-3 mb-6" data-id="element-239">
            {question.options.map((option, index) => {
            const isSelected = selectedAnswer === index;
            const isCorrect = index === question.correct;
            const showResult = showExplanation;
            return <button key={index} onClick={() => handleAnswer(index)} disabled={showExplanation} className={`w-full text-left p-4 rounded-xl border-2 transition-all ${showResult ? isCorrect ? 'bg-green-50 dark:bg-green-900/20 border-green-500' : isSelected ? 'bg-red-50 dark:bg-red-900/20 border-red-500' : 'bg-gray-50 dark:bg-slate-700 border-gray-200 dark:border-slate-600' : isSelected ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-500' : 'bg-gray-50 dark:bg-slate-700 border-gray-200 dark:border-slate-600 hover:border-blue-300 dark:hover:border-blue-500'} ${showExplanation ? 'cursor-default' : 'cursor-pointer'}`} data-id="element-240">
                  <div className="flex items-center justify-between" data-id="element-241">
                    <span className="font-medium text-gray-900 dark:text-white" data-id="element-242">
                      {option}
                    </span>
                    {showResult && isCorrect && <Check className="w-5 h-5 text-green-600 dark:text-green-400" data-id="element-243" />}
                    {showResult && isSelected && !isCorrect && <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" data-id="element-244" />}
                  </div>
                </button>;
          })}
          </div>
          {/* Explanation */}
          {showExplanation && <div className="bg-blue-50 dark:bg-blue-900/30 border-l-4 border-blue-500 p-4 rounded mb-6 animate-fadeIn" data-id="element-245">
              <p className="text-sm text-blue-900 dark:text-blue-200" data-id="element-246">
                {question.explanation}
              </p>
            </div>}
          {/* Next Button */}
          {showExplanation && <button onClick={handleNext} className="w-full py-3 bg-gradient-to-r from-green-600 to-blue-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all" data-id="element-247">
              {currentQuestion < questions.length - 1 ? 'Next Question →' : 'See Results 🎉'}
            </button>}
        </div>
      </div>
    </div>;
}